import { useCallback, useEffect, useMemo } from 'react';

import { usePlayerStore } from '@air/component-player';

import { useCurrentPlayerTimeGetter } from '~/components/AnnotationsV2/hooks/useCurrentPlayerTimeGetter';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';

/**
 * The default comment range spans this fraction of the video duration, centered on the playhead.
 */
const RANGE_DURATION_FRACTION = 0.1;

export type CommentTimeMode = 'range' | 'timestamp' | 'none';

type UseCommentTimeModeArgs = {
  canSelectTimeRange?: boolean;
  shouldEnableTimestamps: boolean;
  setShouldEnableTimestamps: (enable: boolean) => void;
};

export const useCommentTimeMode = ({
  canSelectTimeRange = true,
  shouldEnableTimestamps,
  setShouldEnableTimestamps,
}: UseCommentTimeModeArgs) => {
  const duration = usePlayerStore((state) => state.duration);
  const commentRange = useAnnotationV2Store((state) => state.commentRange);
  const setCommentRange = useAnnotationV2Store((state) => state.setCommentRange);
  const setCommentRangeEnabled = useAnnotationV2Store((state) => state.setCommentRangeEnabled);
  const { getCurrentPlayerTime } = useCurrentPlayerTimeGetter();
  const hasCommentRange = !!commentRange;
  const hasSelectedCommentRange = !!commentRange && commentRange.startTime !== commentRange.endTime;

  useEffect(() => {
    if (!canSelectTimeRange && hasCommentRange) {
      setCommentRange(null);
      setCommentRangeEnabled(false);
    }
  }, [canSelectTimeRange, hasCommentRange, setCommentRange, setCommentRangeEnabled]);

  const timeMode = useMemo<CommentTimeMode>(() => {
    if (!shouldEnableTimestamps) {
      return 'none';
    }

    if (canSelectTimeRange && hasSelectedCommentRange) {
      return 'range';
    }

    return 'timestamp';
  }, [canSelectTimeRange, hasSelectedCommentRange, shouldEnableTimestamps]);

  const selectTimeMode = useCallback(
    (mode: CommentTimeMode) => {
      if (mode === timeMode) {
        return;
      }

      switch (mode) {
        case 'range': {
          if (!canSelectTimeRange) {
            setShouldEnableTimestamps(true);
            setCommentRangeEnabled(false);
            setCommentRange(null);
            break;
          }

          setShouldEnableTimestamps(true);
          const playhead = getCurrentPlayerTime();

          if (duration > 0) {
            const width = duration * RANGE_DURATION_FRACTION;
            const startTime = Math.max(0, Math.min(playhead - width / 2, duration - width));
            const endTime = startTime + width;

            setCommentRange({ startTime, endTime });
          } else {
            setCommentRange({ startTime: playhead, endTime: playhead });
          }

          setCommentRangeEnabled(true);
          break;
        }
        case 'timestamp': {
          const playhead = getCurrentPlayerTime();

          setShouldEnableTimestamps(true);
          if (canSelectTimeRange) {
            setCommentRangeEnabled(true);
            setCommentRange({ startTime: playhead, endTime: playhead });
          } else {
            setCommentRangeEnabled(false);
            setCommentRange(null);
          }
          break;
        }
        case 'none': {
          setShouldEnableTimestamps(false);
          setCommentRangeEnabled(false);
          setCommentRange(null);
          break;
        }
      }
    },
    [
      timeMode,
      canSelectTimeRange,
      duration,
      getCurrentPlayerTime,
      setShouldEnableTimestamps,
      setCommentRange,
      setCommentRangeEnabled,
    ],
  );

  return { timeMode, selectTimeMode };
};
